import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/homepage';
import { categoryDetails, categoryFAQs } from '@/mocks/pages';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) return <NotFound />;

  const detail = categoryDetails[slug];
  const faqs = categoryFAQs[slug] || [];

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.majiang-online.com/' },
        { '@type': 'ListItem', position: 2, name: '玩法分类', item: 'https://www.majiang-online.com/category/guobiao-majiang' },
        { '@type': 'ListItem', position: 3, name: category.name },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    },
  ];

  const relatedCategories = categories.filter((c) => c.slug !== slug).slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${category.name} - 麻将在线玩免费网页版 | ${category.name}在线免费玩`}
        description={`${category.description} ${category.name}是麻将在线玩平台热门玩法之一，${category.playerCount}参与，适合${category.difficulty}玩家。无需下载，打开浏览器即可免费畅玩${category.name}。`}
        keywords={`${category.name},${category.name}在线免费,${category.name}网页版,麻将在线玩免费网页版`}
        canonicalUrl={`https://www.majiang-online.com/category/${slug}`}
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-400">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><Link to="/category/guobiao-majiang" className="hover:text-primary-500 transition-colors cursor-pointer">玩法分类</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-foreground-700 font-medium">{category.name}</li>
          </ol>
        </nav>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="lg:w-2/3">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm px-3 py-1 bg-primary-100/70 text-primary-700 rounded-full font-medium">
                    热度 {category.popularity}%
                  </span>
                  <span className="text-sm px-3 py-1 bg-accent-100/70 text-accent-700 rounded-full font-medium">
                    {category.difficulty}
                  </span>
                  <span className="text-sm px-3 py-1 bg-secondary-100/70 text-secondary-700 rounded-full font-medium">
                    {category.playerCount}
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  {category.name}
                </h1>
                {detail ? (
                  <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-8">
                    {detail.intro}
                  </p>
                ) : (
                  <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-8">
                    {category.description}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <a href="#" className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold text-center hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                    立即免费开玩
                  </a>
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="relative rounded-xl overflow-hidden h-64 lg:h-full min-h-[250px]">
                  {slug === 'guobiao-majiang' && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20mahjong%20game%20scene%20with%20warm%20ambient%20lighting%20on%20polished%20wooden%20table%2C%20traditional%20Chinese%20decorative%20elements%2C%20golden%20and%20burgundy%20color%20palette%2C%20luxurious%20atmosphere%2C%20editorial%20photography%20style%2C%20high%20quality%20composition&width=600&height=500&seq=cat-detail-guobiao&orientation=portrait"
                      alt={category.name}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  {slug === 'sichuan-majiang' && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Traditional%20Chinese%20mahjong%20tiles%20arranged%20on%20dark%20polished%20wooden%20table%2C%20warm%20ambient%20lighting%2C%20elegant%20Chinese%20tea%20set%20alongside%2C%20golden%20and%20burgundy%20accents%2C%20editorial%20photography%2C%20clean%20composition&width=600&height=500&seq=cat-detail-sichuan&orientation=portrait"
                      alt={category.name}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  {slug === 'guangdong-majiang' && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Luxurious%20mahjong%20game%20setup%20with%20ivory%20tiles%20on%20rich%20mahogany%20table%2C%20warm%20golden%20lighting%2C%20traditional%20Chinese%20calligraphy%20scrolls%20in%20background%2C%20elegant%20atmosphere%2C%20editorial%20style&width=600&height=500&seq=cat-detail-guangdong&orientation=portrait"
                      alt={category.name}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  {slug === 'xueliu-chenghe' && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Dynamic%20mahjong%20game%20in%20action%20with%20multiple%20tile%20arrangements%2C%20warm%20ambient%20lighting%2C%20sleek%20modern%20wooden%20table%2C%20energetic%20atmosphere%2C%20golden%20and%20crimson%20tones%2C%20editorial%20photography&width=600&height=500&seq=cat-detail-xueliu&orientation=portrait"
                      alt={category.name}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  {slug === 'changsha-majiang' && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Traditional%20mahjong%20game%20on%20vintage%20wooden%20table%2C%20warm%20candlelight%20ambience%2C%20Chinese%20cultural%20decor%2C%20rich%20textures%2C%20golden%20and%20burgundy%20color%20palette%2C%20artistic%20composition&width=600&height=500&seq=cat-detail-changsha&orientation=portrait"
                      alt={category.name}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  {slug === 'dongbei-majiang' && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20cozy%20mahjong%20scene%20with%20handcrafted%20tiles%20on%20rustic%20wooden%20table%2C%20soft%20winter%20light%2C%20traditional%20northeastern%20Chinese%20decor%2C%20elegant%20composition%2C%20editorial%20quality&width=600&height=500&seq=cat-detail-dongbei&orientation=portrait"
                      alt={category.name}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {detail && (
          <>
            <section className="w-full py-12 md:py-16 bg-background-100">
              <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                      <i className="ri-file-list-3-line text-primary-500 mr-2"></i>
                      基本规则
                    </h2>
                    <ul className="flex flex-col gap-3">
                      {detail.rules.map((rule, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600 text-xs font-bold shrink-0 mt-0.5">
                            {idx + 1}
                          </span>
                          <span className="text-sm md:text-base text-foreground-600 leading-relaxed">{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                      <i className="ri-lightbulb-line text-accent-500 mr-2"></i>
                      实战技巧
                    </h2>
                    <ul className="flex flex-col gap-3">
                      {detail.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600 text-xs font-bold shrink-0 mt-0.5">
                            {idx + 1}
                          </span>
                          <span className="text-sm md:text-base text-foreground-600 leading-relaxed">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="w-full py-12 md:py-16 bg-background-50">
              <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  <i className="ri-star-line text-secondary-500 mr-2"></i>
                  玩法特色
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {detail.features.map((feature, idx) => (
                    <div key={idx} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100/70 text-secondary-600 mb-3">
                        <i className="ri-check-double-line text-lg"></i>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {faqs.length > 0 && (
          <FAQAccordion faqs={faqs} />
        )}

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              探索更多玩法
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedCategories.map((rc) => (
                <Link
                  key={rc.slug}
                  to={`/category/${rc.slug}`}
                  className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 mb-3">
                    <i className={`${rc.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors">
                    {rc.name}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2">{rc.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function FAQAccordion({ faqs }: { faqs: { question: string; answer: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="w-full py-12 md:py-16 bg-background-50">
      <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
          常见问题
        </h2>
        <div className="flex flex-col gap-3">
          {faqs.map((faq, idx) => (
            <div key={idx} className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-sm md:text-base font-medium text-foreground-800">{faq.question}</span>
                <span className={`w-6 h-6 flex items-center justify-center shrink-0 rounded-full transition-all duration-300 ${
                  openIndex === idx ? 'bg-primary-100 text-primary-600 rotate-45' : 'bg-background-200/70 text-foreground-400'
                }`}>
                  <i className="ri-add-line text-sm"></i>
                </span>
              </button>
              <div className={`px-5 overflow-hidden transition-all duration-300 ${
                openIndex === idx ? 'max-h-60 pb-4' : 'max-h-0'
              }`}>
                <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}